import { Check, ChevronDown } from 'lucide-react';
import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FILTER_STATUS } from '@/utils/constants';
import { SEARCH_PARAM_ROUTE_TYPE } from '@/utils/constants/queries';

interface RouteStatusFilterProps {
  isToday: boolean;
}

const RouteStatusFilter: FC<RouteStatusFilterProps> = ({ isToday }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const routeStatuses = [
    {
      value: FILTER_STATUS.ALL,
      label: t('filters.all'),
    },
    ...(isToday
      ? [
          {
            value: FILTER_STATUS.ACTIVE,
            label: t('filters.active'),
          },
          {
            value: FILTER_STATUS.INACTIVE,
            label: t('filters.inactive'),
          },
          {
            value: FILTER_STATUS.COMPLETED,
            label: t('filters.completed'),
          },
        ]
      : []),
    {
      value: FILTER_STATUS.COCKPIT_RIDES,
      label: t('filters.cockpit_rides'),
    },
    {
      value: FILTER_STATUS.DANGEROUS_GOODS,
      label: t('filters.dangerous_goods'),
    },
  ];

  const selectedStatus = searchParams.get(SEARCH_PARAM_ROUTE_TYPE) || FILTER_STATUS.ALL;

  const selectedRouteTypeLabel =
    routeStatuses.find((type) => type.value === selectedStatus)?.label || routeStatuses[0].label;

  const onChangeRouteType = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set(SEARCH_PARAM_ROUTE_TYPE, value);

        return newParams;
      });
    },
    [searchParams]
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 cursor-pointer border-0 bg-secondary rounded-full px-4 py-2">
        <span className="text-sm font-medium">{selectedRouteTypeLabel}</span>
        <ChevronDown className="size-5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" avoidCollisions>
        {routeStatuses.map(({ label, value }) => (
          <DropdownMenuItem key={value} onClick={() => onChangeRouteType(value)}>
            <span>{label}</span>
            {selectedStatus === value && <Check className="ml-auto" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RouteStatusFilter;
